"use client";

import { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User 
} from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { UserRole } from '../types/user';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isManager: boolean;
  userRole: UserRole | null;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  isAdmin: false,
  isManager: false,
  userRole: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user?.email);
      
      if (user) {
        try {
          // Test token refresh to catch any issues early
          await user.getIdToken(true).catch(async (error) => {
            console.error('Token refresh failed:', error);
            // If token refresh fails, sign out the user
            await signOut();
            throw new Error('Authentication session expired. Please sign in again.');
          });

          setUser(user);
          
          // Create user document if it doesn't exist
          const userDocRef = doc(db, 'users', user.uid);
          const userData = {
            email: user.email,
            role: 'user' as UserRole,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
          };

          try {
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              console.log('User document exists:', userDocSnap.data());
              const role = userDocSnap.data()?.role || 'user';
              setUserRole(role as UserRole);
              // Update last login
              await setDoc(userDocRef, { lastLogin: new Date().toISOString() }, { merge: true });
            } else {
              console.log('Creating new user document');
              await setDoc(userDocRef, userData);
              setUserRole('user');
            }
          } catch (error) {
            console.error('Error accessing user document:', error);
            // Create the document if there was an error (might be permissions)
            await setDoc(userDocRef, userData);
            setUserRole('user');
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          setUserRole('user');
        }
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      console.log('Attempting sign in from domain:', window.location.hostname);
      console.log('Using Firebase authDomain:', process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN);
      
      // Add retry logic for token issues
      const maxRetries = 2;
      let lastError;
      
      for (let i = 0; i <= maxRetries; i++) {
        try {
          const result = await signInWithPopup(auth, provider);
          
          // Verify token refresh works
          await result.user.getIdToken(true);
          
          // Save user data to Firestore
          const userRef = doc(db, 'users', result.user.uid);
          await setDoc(userRef, {
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL,
            lastSignIn: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }, { merge: true });
          
          return result.user;
        } catch (error: any) {
          lastError = error;
          if (error.code === 'auth/network-request-failed') {
            console.log(`Retry attempt ${i + 1} of ${maxRetries + 1}`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
            continue;
          }
          throw error;
        }
      }
      
      // If we get here, all retries failed
      throw lastError;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Handle specific error cases
      if (error.code === 'auth/unauthorized-domain' || error.code === 'auth/auth-domain-config-required') {
        throw new Error(
          `This domain (${window.location.hostname}) is not authorized for authentication. Please add it to Firebase authorized domains.`
        );
      }
      
      if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error occurred. Please check your internet connection and try again.');
      }
      
      // Handle other authentication errors
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const isAdmin = userRole === 'admin';
  const isManager = userRole === 'manager';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      signInWithGoogle, 
      signOut,
      isAdmin,
      isManager,
      userRole 
    }}>
      {children}
    </AuthContext.Provider>
  );
} 